/**
 * The first playable room, as data. Pure TypeScript: the scene builds visuals and static bodies
 * from these rectangles (generated textures, no image files) and `validateRoom` is unit-tested
 * against the tuning so the layout is provably playable (every platform is jumpable, the spawn
 * stands on ground, the pit is a real gap, signs name real N1 actions).
 *
 * Coordinates are world px; a platform's (x, y) is the top-left of its collision rectangle, so
 * `y` is the walkable top surface. 'one-way' platforms collide only on their top.
 */
import {
  N1_ACTIONS,
  N1_TIER_LABELS,
  type N1ActionId,
  type N1TierLabel,
} from '../assets/n1Manifest';
import { N1_TIER } from '../config';
import type { Facing } from '../player/stateMachine';
import { LOOP_ACTIONS } from '../player/tuning';

export type PlatformKind = 'ground' | 'solid' | 'one-way';

export interface RoomPlatform {
  id: string;
  kind: PlatformKind;
  x: number;
  y: number;
  w: number;
  h: number;
}

/** A hint post in the world naming the N1 action the spot demonstrates. */
export interface RoomSign {
  id: string;
  x: number;
  text: string;
  action: N1ActionId;
}

export interface RoomData {
  id: string;
  name: string;
  width: number;
  height: number;
  /** Tier the room was tuned on (the game loads config N1_TIER; both must be known labels). */
  tier: N1TierLabel;
  /** Top surface of the main ground. */
  groundY: number;
  spawn: { x: number; feetY: number; facing: Facing; action: N1ActionId };
  /** Feet below this world y = fell into the pit → deterministic respawn at `spawn`. */
  respawnBelowY: number;
  /** The gap in the ground (world x range with no floor). */
  pit: { left: number; right: number };
  platforms: RoomPlatform[];
  signs: RoomSign[];
}

const GROUND_Y = 620;

export const DEMO_ROOM: RoomData = {
  id: 'demo-room',
  name: 'N1 demo room',
  width: 3200,
  height: 720,
  tier: N1_TIER,
  groundY: GROUND_Y,
  spawn: { x: 200, feetY: GROUND_Y, facing: 'right', action: 'idle-loop' },
  respawnBelowY: 720 + 120,
  pit: { left: 1400, right: 1640 },
  platforms: [
    { id: 'ground-west', kind: 'ground', x: 0, y: GROUND_Y, w: 1400, h: 100 },
    { id: 'ground-east', kind: 'ground', x: 1640, y: GROUND_Y, w: 1560, h: 100 },
    { id: 'step', kind: 'solid', x: 2600, y: GROUND_Y - 48, w: 160, h: 48 },
    { id: 'ledge-west', kind: 'one-way', x: 1000, y: GROUND_Y - 96, w: 260, h: 20 },
    { id: 'ledge-east', kind: 'one-way', x: 2150, y: GROUND_Y - 110, w: 300, h: 20 },
  ],
  signs: [
    { id: 'sign-move', x: 440, text: 'A/D or ←/→ walk · hold Shift to run', action: 'walk-loop' },
    {
      id: 'sign-ledge',
      x: 1130,
      text: 'one-way ledge: jump through it from below',
      action: 'jump-to-idle',
    },
    {
      id: 'sign-pit',
      x: 1520,
      text: 'pit: run + Space to cross, fall in → respawn',
      action: 'jump-to-run',
    },
    {
      id: 'sign-turn',
      x: 1900,
      text: 'reverse while moving: walk-turn / run-turn',
      action: 'run-turn',
    },
    { id: 'sign-step', x: 2680, text: 'Space hops onto the step', action: 'run-to-jump' },
    {
      id: 'sign-brake',
      x: 3020,
      text: 'release the keys: brake to idle',
      action: 'run-to-brake-to-idle',
    },
  ],
};

/** Feet must clear a platform top by this much at the apex for a landing to be reliable. */
export const LANDING_CLEARANCE_PX = 8;

export interface RoomChecks {
  /** World px a jump rises (from tuning). */
  jumpApexPx: number;
  /** Clearance the feet need above a platform top to land on it. */
  landingClearancePx: number;
}

const overlaps = (a: RoomPlatform, b: RoomPlatform) =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

/** Every problem with a room (empty = valid). */
export function validateRoom(room: RoomData, checks: RoomChecks): string[] {
  const problems: string[] = [];
  if (!(room.width > 0) || !(room.height > 0)) problems.push('room size must be positive');
  if (!(N1_TIER_LABELS as readonly string[]).includes(room.tier))
    problems.push(`tier ${room.tier} is not an N1 tier`);
  if (!(N1_ACTIONS as readonly string[]).includes(room.spawn.action))
    problems.push(`spawn action ${room.spawn.action} is not an N1 action`);
  else if (!(LOOP_ACTIONS as readonly string[]).includes(room.spawn.action))
    problems.push(`spawn action ${room.spawn.action} must be a loop`);
  if (room.respawnBelowY <= room.height) problems.push('respawnBelowY must lie below the room');
  if (!(room.pit.left < room.pit.right)) problems.push('pit must have a positive width');

  const ids = new Set<string>();
  for (const p of room.platforms) {
    if (ids.has(p.id)) problems.push(`platform id ${p.id} duplicated`);
    ids.add(p.id);
    if (!(p.w > 0) || !(p.h > 0)) problems.push(`${p.id}: size must be positive`);
    if (p.x < 0 || p.y < 0 || p.x + p.w > room.width || p.y + p.h > room.height)
      problems.push(`${p.id}: leaves the room`);
    const rise = room.groundY - p.y;
    if (p.kind !== 'ground' && rise >= checks.jumpApexPx - checks.landingClearancePx)
      problems.push(
        `${p.id}: top is ${rise} px above the ground, the jump apex ${checks.jumpApexPx.toFixed(0)} px cannot land on it`,
      );
    if (p.kind === 'ground' && p.y !== room.groundY)
      problems.push(`${p.id}: ground platforms must sit on groundY`);
    if (p.x < room.pit.right && room.pit.left < p.x + p.w && p.y <= room.groundY)
      problems.push(`${p.id}: fills the pit`);
  }
  for (let i = 0; i < room.platforms.length; i++)
    for (let j = i + 1; j < room.platforms.length; j++) {
      const a = room.platforms[i]!;
      const b = room.platforms[j]!;
      if (overlaps(a, b)) problems.push(`${a.id} overlaps ${b.id}`);
    }

  const standsOn = room.platforms.some(
    (p) => p.y === room.spawn.feetY && p.x <= room.spawn.x && room.spawn.x <= p.x + p.w,
  );
  if (!standsOn) problems.push('spawn does not stand on a platform top');
  if (room.spawn.x >= room.pit.left && room.spawn.x <= room.pit.right)
    problems.push('spawn is above the pit');

  const signIds = new Set<string>();
  for (const s of room.signs) {
    if (signIds.has(s.id)) problems.push(`sign id ${s.id} duplicated`);
    signIds.add(s.id);
    if (!(N1_ACTIONS as readonly string[]).includes(s.action))
      problems.push(`${s.id}: action ${s.action} is not an N1 action`);
    if (s.x < 0 || s.x > room.width) problems.push(`${s.id}: leaves the room`);
    if (!s.text.trim()) problems.push(`${s.id}: empty text`);
  }
  return problems;
}
